import
{
    Component,
    EventEmitter,
    Input,
    ViewChild,
    OnInit,
    Output,
    AfterViewInit,
    ElementRef,
} from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { Command, IconCommand } from "@tylertech/commands-module";
import { FileService } from "@tylertech/core-module";
import { MetadataPropertyService } from "../../services";
import { ValueChangedParms } from "../../interfaces";
import { IDialogData, MetadataPhotoCropDialogComponent } from "./metadataPhotoCropDialog.component";
import { DialogService, IDialogOptions } from "@tylertech/forge-angular";

const IMAGE_PREFIX = /^data:image\/(jpeg|jpg|png|gif|bmp|tiff|emf|wmf);base64,/;

@Component({
    selector: "metadata-photo",
    templateUrl: "./metadataPhoto.tmpl.html",
    styleUrls: ["./metadataPhoto.scss"],
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataPhotoComponent implements OnInit, AfterViewInit
{
    @Input()
    name: string;

    @Input()
    metadata: PropertyMetadata;

    @Input()
    model: string;

    @Input()
    parent: any;

    @Input()
    viewOnly: boolean = false;

    @Input()
    dense: boolean;

    @Input()
    isMenu: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    commands: Command[];

    @ViewChild("fileInput")
    private readonly fileInputRef: ElementRef<HTMLInputElement>;

    private isInitialized: boolean = false;

    constructor(
        private readonly $fileService: FileService,
        private readonly $metadataPropertyService: MetadataPropertyService,
        private dialogService: DialogService
    ) { }

    get Photo(): string
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(this.model))
        {
            if (!Utilities.IsNullOrUndefined(this.metadata.DefaultPhotoUrl)) return this.metadata.DefaultPhotoUrl;
            return "https://cdn.forge.tylertech.com/v1/images/spot/no-apps-spot.svg";
        }
        return this.model.match(IMAGE_PREFIX) ? this.model : `data:image/jpeg;base64,${this.model}`;
    }

    private get fileInput(): HTMLInputElement
    {
        return this.fileInputRef.nativeElement;
    }

    get altText(): string
    {
        return !this.model ? "Placeholder for upload." : "";
    }

    ngAfterViewInit(): void
    {
        if (this.isInitialized) return;
        this.isInitialized = true;
    }

    ngOnInit(): void
    {
        const self = this;
        this.commands = [
            new IconCommand({
                icon: "file_upload",
                action: () => self.selectNewFile(),
                getEnabled: () => !self.isDisabled(),
                tooltip: this.photoLabel
            }),
            new IconCommand({
                icon: "crop",
                action: () => self.cropImage(),
                getEnabled: () => !self.isDisabled() && !Utilities.IsNullOrUndefinedOrEmpty(this.model),
                tooltip: "Crop image"
            }),
            new IconCommand({
                icon: "clear",
                action: () => self.clearFile(),
                getEnabled: () => !self.isDisabled() && !Utilities.IsNullOrUndefinedOrEmpty(this.model),
                tooltip: "Clear"
            }),
        ];
    }

    get allowedTypes(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadata.AllowedTypes)) return "image/*";
        return this.metadata.AllowedTypes;
    }

    get photoLabel(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadata.Label)) return "Upload";
        return Utilities.toSentenceCase(this.metadata.Label);
    }

    selectNewFile(): void
    {
        this.fileInput.click();
    }

    clearFile(): void
    {
        this.model = null;
        requestAnimationFrame(() => this.onValueChanged.emit({ value: null }));
    }

    cropImage(): void
    {
        const options: IDialogOptions = {
            preset: 'dialog',
        };

        const data: IDialogData = {
            photo: this.model
        };

        const dialogRef = this.dialogService.open(MetadataPhotoCropDialogComponent, { options, data });

        dialogRef.afterClosed.subscribe((result: string) =>
        {
            if (result)
            {
                this.model = result.substring(result.indexOf(",") + 1);
                this.updateModel(true);
            }
        });
    }

    updateModel(cropped = false): void
    {
        const self = this;

        if (cropped)
        {
            this.onValueChanged.emit({ value: self.model });
        }
        else
        {
            this.$fileService.convertToBase64String(this.fileInput.files[0]).then(data =>
            {
                self.model = data.match(IMAGE_PREFIX) ? data.substring(data.indexOf(",") + 1) : data;
                this.onValueChanged.emit({ value: self.model });
            });
        }
    }

    isDisabled(): boolean
    {
        return (
            this.metadata.Disabled ||
            this.viewOnly ||
            this.$metadataPropertyService.isViewOnly(this.metadata, this.parent) ||
            this.$metadataPropertyService.isBeingRecalculated(this.metadata, this.parent)
        );
    }
}
